import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  FileDown,
  Loader2,
  Lock,
  RefreshCw,
  Sparkles,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GenerationTimeline } from "@/components/GenerationTimeline";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { t, tf } from "@/i18n";
import { OverviewTab } from "@/components/OverviewTab";
import { GuestsTab } from "@/components/GuestsTab";
import { InvitationsTab } from "@/components/InvitationsTab";
import { PlanBoard } from "@/components/PlanBoard";
import {
  errorMessage,
  isUnauthorized,
  login,
  setActiveWedding,
  setToken,
  getJob,
  getPlan,
  getWedding,
  listGuests,
  lockTable,
  moveGuest,
  patchWedding,
  pinGuest,
  renameTable,
  suggestMoves,
  planPdfUrl,
  refreshNotes,
  startGeneration,
} from "@/lib/api";

const EMPTY_STATE_IMAGE =
  "https://images.unsplash.com/photo-1737749685390-0959c05aecbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwYm90YW5pY2FsJTIwbGluZSUyMGFydCUyMHdlZGRpbmclMjBpbnZpdGF0aW9ufGVufDB8fHx8MTc4NjYxNjU4OXww&ixlib=rb-4.1.0&q=85";

export default function Admin() {
  const { weddingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "plan";
  const jobParam = searchParams.get("job");

  const [payload, setPayload] = useState(null);
  const [guests, setGuests] = useState([]);
  const [plan, setPlan] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const pollRef = useRef(null);

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const loadPlan = useCallback(async () => {
    try {
      const data = await getPlan(weddingId);
      setPlan(data.plan);
      return data.plan;
    } catch (error) {
      setPlan(null);
      return null;
    }
  }, [weddingId]);

  const loadAll = useCallback(async () => {
    try {
      const [weddingPayload, guestPayload] = await Promise.all([
        getWedding(weddingId),
        listGuests(weddingId),
      ]);
      setPayload(weddingPayload);
      setGuests(guestPayload.guests);
      if (weddingPayload.has_plan) await loadPlan();
      if (weddingPayload.job && ["queued", "running"].includes(weddingPayload.job.status)) {
        setJob(weddingPayload.job);
      }
    } catch (error) {
      if (isUnauthorized(error)) setLocked(true);
      else setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [weddingId, loadPlan]);

  useEffect(() => {
    setActiveWedding(weddingId);
    loadAll();
  }, [loadAll, weddingId]);

  const unlock = async (event) => {
    event.preventDefault();
    setUnlocking(true);
    try {
      const { token } = await login(weddingId, password);
      setToken(weddingId, token);
      setLocked(false);
      setPassword("");
      setLoading(true);
      await loadAll();
    } catch (error) {
      toast.error(errorMessage(error, t("admin.wrongPassword")));
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (!jobParam || job) return;
    getJob(jobParam)
      .then(setJob)
      .catch(() => {});
  }, [jobParam, job]);

  // poll a running generation
  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await getJob(job.id);
        setJob(fresh);
        if (fresh.status === "done") {
          clearInterval(pollRef.current);
          const freshPlan = await loadPlan();
          const weddingPayload = await getWedding(weddingId);
          setPayload(weddingPayload);
          toast.success(tf("admin.planReady", { s: fresh.elapsed }), {
            description: freshPlan
              ? tf("admin.planReadyDesc", {
                  seated: freshPlan.stats.seated,
                  conflicts: freshPlan.validation.conflict_count,
                })
              : undefined,
          });
          const params = new URLSearchParams(searchParams);
          params.delete("job");
          params.set("tab", "plan");
          setSearchParams(params, { replace: true });
        }
        if (fresh.status === "error") {
          clearInterval(pollRef.current);
          toast.error(t("admin.generationFailed"), { description: fresh.error });
        }
      } catch (error) {
        clearInterval(pollRef.current);
      }
    }, 1200);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  const generate = async () => {
    setStarting(true);
    try {
      const fresh = await startGeneration(weddingId);
      setJob(fresh);
      setTab("plan");
      toast.info(t("admin.building"), { description: t("admin.buildingDesc") });
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotStart")));
    } finally {
      setStarting(false);
    }
  };

  const requestGenerate = () => {
    if (plan?.edited) {
      setConfirmRegenerate(true);
      return;
    }
    generate();
  };

  const onMove = async (guestId, targetTable, moveParty = false) => {
    try {
      const data = await moveGuest(weddingId, {
        guest_id: guestId,
        target_table: targetTable,
        move_party: moveParty,
      });
      setPlan(data.plan);
      const conflicts = data.plan.validation.conflict_count;
      const over = data.plan.validation.over_capacity_tables || [];
      if (conflicts) {
        toast.error(tf("admin.conflicts", { n: conflicts }), {
          description: t("admin.conflictsDesc"),
        });
      } else if (over.length) {
        toast.warning(tf("admin.overCapacity", { t: over.join(", ") }));
      }
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotMove")));
    }
  };

  const onToggleLock = async (table, isLocked) => {
    try {
      const data = await lockTable(weddingId, table, isLocked);
      setPlan(data.plan);
      toast.success(
        isLocked ? tf("admin.locked", { t: table }) : tf("admin.unlocked", { t: table })
      );
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotLock")));
    }
  };

  const onTogglePin = async (guestId, pinned) => {
    try {
      const data = await pinGuest(weddingId, guestId, pinned);
      setPlan(data.plan);
      const guest = (data.plan.guests || []).find((item) => item.id === guestId);
      const name = guest?.name || t("admin.guest");
      toast.success(
        pinned
          ? tf("admin.pinned", { name, t: guest?.table })
          : tf("admin.unpinned", { name })
      );
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotPin")));
    }
  };

  const onRename = async (table, title) => {
    try {
      const data = await renameTable(weddingId, table, title);
      setPlan(data.plan);
      toast.success(title ? tf("admin.renamed", { title }) : t("admin.nameRestored"));
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotRename")));
    }
  };

  const refreshAiNotes = async () => {
    setRefreshing(true);
    try {
      const data = await refreshNotes(weddingId);
      setPlan(data.plan);
      toast.success(
        data.refreshed
          ? tf("admin.notesRewritten", { n: data.refreshed })
          : t("admin.notesFresh")
      );
    } catch (error) {
      toast.error(errorMessage(error, t("admin.cannotRefresh")));
    } finally {
      setRefreshing(false);
    }
  };

  const exportPdf = () => {
    window.open(planPdfUrl(weddingId), "_blank", "noopener");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-[color:var(--muted-ink)]" />
          <p className="mt-3 text-[13px] text-[color:var(--muted-ink)]">{t("admin.opening")}</p>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <form
          onSubmit={unlock}
          className="surface w-full max-w-[420px] rounded-xl p-8"
          data-testid="admin-locked"
        >
          <div className="flex items-center justify-between">
            <span
              className="flex h-11 w-11 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--paper-2)" }}
            >
              <Lock className="h-5 w-5" style={{ color: "var(--gild)" }} />
            </span>
            <LanguageSwitch />
          </div>
          <h1 className="font-display mt-4 text-2xl font-semibold">{t("admin.private")}</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
            {t("admin.privateHint")}
          </p>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t("admin.passwordPlaceholder")}
            autoFocus
            autoComplete="current-password"
            className="mt-5 h-11 w-full rounded-lg border bg-white px-3 text-[15px]"
            style={{ borderColor: "var(--line)" }}
            data-testid="admin-password-input"
          />
          <Button
            type="submit"
            className="mt-3 h-11 w-full"
            disabled={unlocking || !password}
            data-testid="admin-unlock-button"
          >
            {unlocking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("admin.open")}
          </Button>
          <p className="mt-4 text-[12px] text-[color:var(--muted-ink)]">{t("admin.guestsNoNeed")}</p>
        </form>
      </div>
    );
  }

  if (notFound || !payload) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="surface max-w-md rounded-xl p-8 text-center">
          <AlertTriangle className="mx-auto h-6 w-6" style={{ color: "var(--danger)" }} />
          <h1 className="mt-3 text-2xl font-semibold">{t("admin.notFound")}</h1>
          <p className="mt-2 text-[13.5px] text-[color:var(--ink-soft)]">{t("admin.notFoundHint")}</p>
          <Button className="mt-5" asChild data-testid="admin-home-button">
            <Link to="/">{t("admin.home")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const wedding = payload.wedding;
  const generating = Boolean(job && ["queued", "running"].includes(job.status));
  const staleNotes = (plan?.tables || []).filter((item) => item.justification_stale).length;
  const lockedTables = (plan?.tables || []).filter((item) => item.locked).length;
  const pinnedGuests = Object.keys(plan?.locks?.pins || {}).length;
  const rsvpUrl = `${window.location.origin}/w/${weddingId}/rsvp`;
  const adminUrl = `${window.location.origin}/w/${weddingId}/admin`;

  return (
    <div className="min-h-screen pb-14">
      <header className="sticky top-0 z-30 border-b border-[color:var(--line)] bg-[color:var(--paper)]">
        <div className="mx-auto flex max-w-[1860px] flex-wrap items-center justify-between gap-3 px-4 py-3 lg:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <Link to="/" className="font-display shrink-0 text-lg font-semibold">
              {t("app.name")}
            </Link>
            <span className="hidden h-6 w-px bg-[color:var(--line)] sm:block" />
            <div className="min-w-0">
              <h1
                className="font-display truncate text-xl font-semibold leading-tight"
                data-testid="admin-wedding-title"
              >
                {wedding.couple_names}
              </h1>
              <p className="truncate text-[11.5px] text-[color:var(--muted-ink)]">
                {[wedding.venue, wedding.wedding_date].filter(Boolean).join(" · ")}
                {wedding.is_demo ? ` · ${t("admin.demo")}` : ""}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <LanguageSwitch />
            <span
              className="font-num rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{ backgroundColor: "var(--paper-2)", color: "var(--ink-soft)" }}
              data-testid="admin-response-rate"
            >
              {payload.counts.answered}/{payload.counts.guests} {t("nav.answered")} ·{" "}
              {payload.counts.response_rate}%
            </span>
            {staleNotes ? (
              <Button
                variant="outline"
                size="sm"
                onClick={refreshAiNotes}
                disabled={refreshing}
                data-testid="refresh-notes-button"
              >
                {refreshing ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                )}
                {tf("admin.refreshNotes", { n: staleNotes })}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={exportPdf}
              disabled={!plan}
              data-testid="export-pdf-button"
            >
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> {t("nav.exportPdf")}
            </Button>
            <Button
              size="sm"
              onClick={requestGenerate}
              disabled={generating || starting || !payload.counts.guests}
              data-testid="generate-plan-button"
            >
              {generating || starting ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : plan ? (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              {plan ? t("nav.regenerate") : t("nav.generate")}
            </Button>
          </div>
        </div>

        <div className="mx-auto max-w-[1860px] px-4 pb-2 lg:px-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[color:var(--paper-2)]">
              <TabsTrigger value="plan" data-testid="tab-plan">
                {t("nav.plan")}
              </TabsTrigger>
              <TabsTrigger value="guests" data-testid="tab-guests">
                {t("nav.guests")} ({payload.counts.guests})
              </TabsTrigger>
              <TabsTrigger value="invitations" data-testid="tab-invitations">
                {t("nav.invitations")}
              </TabsTrigger>
              <TabsTrigger value="overview" data-testid="tab-overview">
                {t("nav.overview")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      <main className="mx-auto max-w-[1860px] px-4 py-6 lg:px-8">
        {tab === "invitations" ? (
          <InvitationsTab
            weddingId={weddingId}
            contactEmail={wedding.contact_email}
            onGoToOverview={() => setTab("overview")}
          />
        ) : null}
        {tab === "overview" ? (
          <OverviewTab
            payload={payload}
            plan={plan}
            rsvpUrl={rsvpUrl}
            adminUrl={adminUrl}
            generating={generating || starting}
            onGenerate={requestGenerate}
            onExportPdf={exportPdf}
            onGoToPlan={() => setTab("plan")}
            onPatch={async (changes) => {
              const fresh = await patchWedding(weddingId, changes);
              setPayload(fresh);
            }}
          />
        ) : null}

        {tab === "guests" ? (
          <GuestsTab
            weddingId={weddingId}
            guests={guests}
            rsvpUrl={rsvpUrl}
            onChanged={async () => {
              const [weddingPayload, guestPayload] = await Promise.all([
                getWedding(weddingId),
                listGuests(weddingId),
              ]);
              setPayload(weddingPayload);
              setGuests(guestPayload.guests);
            }}
          />
        ) : null}

        {tab === "plan" ? (
          generating ? (
            <div className="mx-auto max-w-[620px] py-10" data-testid="generation-panel">
              <div className="surface rounded-xl p-6">
                <p className="eyebrow">{t("admin.working")}</p>
                <h2 className="mt-1 text-2xl font-semibold leading-tight">
                  {tf("admin.seating", { n: payload.counts.guests })}
                </h2>
                <p className="mt-2 text-[13.5px] text-[color:var(--ink-soft)]">{t("admin.seatingHint")}</p>
                <div className="mt-5">
                  <GenerationTimeline
                    steps={job.steps}
                    doneSteps={job.done_steps}
                    currentStep={job.current_step}
                    elapsed={job.elapsed}
                    status={job.status === "queued" ? "running" : job.status}
                  />
                </div>
              </div>
            </div>
          ) : plan ? (
            <PlanBoard
              plan={plan}
              onMove={onMove}
              onToggleLock={onToggleLock}
              onTogglePin={onTogglePin}
              onRename={onRename}
              onSuggest={(ids) => suggestMoves(weddingId, ids)}
              headTable={wedding.head_table}
              childrenTable={wedding.children_table}
            />
          ) : (
            <div className="mx-auto max-w-[720px] py-8" data-testid="plan-empty-state">
              <div className="surface overflow-hidden rounded-xl">
                <img src={EMPTY_STATE_IMAGE} alt="" className="h-[180px] w-full object-cover" />
                <div className="p-6">
                  <p className="eyebrow">{t("admin.noPlan")}</p>
                  <h2 className="mt-1 text-2xl font-semibold">
                    {payload.counts.guests ? t("admin.readyTitle") : t("admin.startTitle")}
                  </h2>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
                    {payload.counts.guests
                      ? tf("admin.readyHint", {
                          guests: payload.counts.guests,
                          answered: payload.counts.answered,
                        })
                      : t("admin.startHint")}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button
                      onClick={generate}
                      disabled={!payload.counts.guests || starting}
                      data-testid="empty-generate-plan-button"
                    >
                      <Sparkles className="mr-2 h-4 w-4" /> {t("admin.generateCta")}
                    </Button>
                    <Button variant="outline" onClick={() => setTab("guests")}>
                      {t("admin.manageGuests")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : null}
      </main>

      <AlertDialog open={confirmRegenerate} onOpenChange={setConfirmRegenerate}>
        <AlertDialogContent data-testid="regenerate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {lockedTables || pinnedGuests ? t("admin.rebuildRest") : t("admin.startOver")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.movedByHand")}{" "}
              {lockedTables || pinnedGuests ? (
                <>
                  {lockedTables ? tf("admin.keptLocked", { n: lockedTables }) : ""}
                  {lockedTables && pinnedGuests ? ` ${t("admin.and")} ` : ""}
                  {pinnedGuests ? tf("admin.keptPinned", { n: pinnedGuests }) : ""}{" "}
                  {t("admin.willBeKept")}
                </>
              ) : (
                t("admin.regenerateLoses")
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regenerate-cancel">{t("admin.keepPlan")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="regenerate-confirm"
              onClick={() => {
                setConfirmRegenerate(false);
                generate();
              }}
            >
              {t("admin.regenerate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
